import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import DualLensCompareView from "../../../components/AiVisibility/DualLens/DualLensCompareView";
import DualLensHeader from "../../../components/AiVisibility/DualLens/DualLensHeader";
import DualLensScoreCard from "../../../components/AiVisibility/DualLens/DualLensScoreCard";
import DualLensProcessing from "../../../components/AiVisibility/DualLens/DualLensProcessing";
import {
	getDualLensJob,
	isDualLensJobComplete,
	pollDualLensJob,
	regenerateDualLensJob,
} from "../../../services/dualLensApi";

const AiVisibilityDualLensView = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const jobId = searchParams.get("jobId");
	const { currentWebsiteId, userLoader, websiteLoader } = useContext(ProductContext);

	const [job, setJob] = useState(null);
	const [phase, setPhase] = useState("idle");
	const [initialLoading, setInitialLoading] = useState(true);
	const [error, setError] = useState(null);
	const [processingLabel, setProcessingLabel] = useState(null);
	const [processingUrl, setProcessingUrl] = useState(null);
	const [processingProgress, setProcessingProgress] = useState(null);
	const [isRegenerating, setIsRegenerating] = useState(false);

	const loadJobFlow = useCallback(
		async (targetJobId) => {
			if (!targetJobId) {
				setJob(null);
				setPhase("idle");
				setInitialLoading(false);
				return;
			}

			if (!currentWebsiteId) {
				return;
			}

			setInitialLoading(true);
			setError(null);
			setJob(null);

			try {
				const previewJob = await getDualLensJob(currentWebsiteId, targetJobId, {
					includeContent: false,
				});

				if (!previewJob.ok || !previewJob.job) {
					setError(previewJob.message || "Comparison not available.");
					setPhase("error");
					return;
				}

				let currentJob = previewJob.job;
				setProcessingUrl(currentJob.url ?? null);

				if (!isDualLensJobComplete(currentJob.status)) {
					setPhase("processing");
					setProcessingLabel(currentJob.status_label || "Processing");
					setProcessingProgress(null);

					const poll = await pollDualLensJob(currentWebsiteId, targetJobId, {
						onTick: (statusData) => {
							if (statusData?.status_label) {
								setProcessingLabel(statusData.status_label);
							}
							if (statusData?.progress != null) {
								setProcessingProgress(statusData.progress);
							}
						},
					});

					if (!poll.ok) {
						setError(poll.message || "Job failed");
						setPhase("error");
						return;
					}
				}

				setPhase("loading");
				setProcessingLabel("Loading comparison");

				const fullJob = await getDualLensJob(currentWebsiteId, targetJobId, {
					includeContent: true,
				});
				if (!fullJob.ok || !fullJob.job?.content) {
					setError(fullJob.message || "Comparison not available.");
					setPhase("error");
					return;
				}

				setJob(fullJob.job);
				setPhase("ready");
			} catch {
				setError("Failed to load comparison.");
				setPhase("error");
			} finally {
				setInitialLoading(false);
				setIsRegenerating(false);
			}
		},
		[currentWebsiteId]
	);

	useEffect(() => {
		if (userLoader || websiteLoader) return;
		if (!jobId) {
			setPhase("idle");
			setInitialLoading(false);
			return;
		}
		if (!currentWebsiteId) return;
		loadJobFlow(jobId);
	}, [jobId, currentWebsiteId, loadJobFlow, userLoader, websiteLoader]);

	const handleRegenerate = useCallback(async () => {
		if (!jobId || isRegenerating || !currentWebsiteId || phase === "processing") return;

		setIsRegenerating(true);

		try {
			const regen = await regenerateDualLensJob(currentWebsiteId, jobId);

			if (!regen.ok || !regen.job?.id) {
				toast.error(regen.message || "Failed to regenerate job");
				setIsRegenerating(false);
				return;
			}

			setJob(null);
			setPhase("processing");
			setProcessingLabel(regen.job.status_label || "Queued");
			setProcessingUrl(regen.job.url ?? job?.url ?? null);
			setProcessingProgress(null);
			setInitialLoading(false);
			setSearchParams({ jobId: String(regen.job.id) });
		} catch {
			toast.error("Failed to regenerate comparison");
			setIsRegenerating(false);
		}
	}, [jobId, isRegenerating, currentWebsiteId, phase, setSearchParams, job?.url]);

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	if (!jobId) {
		return (
			<div className="contentBlock ai-visibility-dashboard dual-lens">
				<p className="text-muted">No job selected.</p>
				<Button color="primary" onClick={() => navigate("/aivisibility/dual-lens")}>
					Back to Dual Lens View
				</Button>
			</div>
		);
	}

	if (jobId && !currentWebsiteId && phase !== "error") {
		return <DetailSkeleton />;
	}

	if (initialLoading && phase === "idle") {
		return <DetailSkeleton />;
	}

	if (phase === "processing") {
		return (
			<DualLensProcessing
				url={processingUrl}
				statusLabel={processingLabel}
				progress={processingProgress}
			/>
		);
	}

	if (phase === "loading") {
		return (
			<div className="contentBlock ai-visibility-dashboard dual-lens dual-lens-view">
				<DetailSkeleton />
			</div>
		);
	}

	if (phase === "error") {
		return (
			<div className="contentBlock ai-visibility-dashboard dual-lens dual-lens-view">
				<p className="text-muted">{error || "Comparison not available."}</p>
				<Button color="primary" onClick={() => navigate("/aivisibility/dual-lens")}>
					Back to Dual Lens View
				</Button>
			</div>
		);
	}

	if (!job) {
		return <DetailSkeleton />;
	}

	return (
		<div className="contentBlock ai-visibility-dashboard dual-lens dual-lens-view">
			<DualLensHeader
				url={job.url}
				status={job.status}
				statusLabel={job.status_label}
				isProcessing={isRegenerating || phase === "processing"}
				onRegenerate={handleRegenerate}
			/>
			<DualLensScoreCard url={job.url} status={job.status} content={job.content} />
			<DualLensCompareView content={job.content} />
		</div>
	);
};

export default AiVisibilityDualLensView;
